import { Fragment, useEffect, useState, type ReactNode } from 'react'
import type { AdminWorkerSearchHit, WorkerSearchHit, WorkerSearchResults as SearchResults } from '../index.js'
import { TIER_BG_CLASS, TIER_LABELS } from '../../core/index.js'
import { WORKER_ROLE_BADGE_CLASS, WORKER_ROLE_LABELS } from '../../core/index.js'
import { formatScore } from '../../core/index.js'
import { WorkerIcon } from '../../core/index.js'
import { SortableTh, TIER_SORT_RANK, useSort, type SortAccessors } from '../../core/index.js'

/** Mirrors the API's rule: a shorter query is rejected, so it is never sent. */
/** The API's own minimum: fewer letters start too many names to be a search. */
export const MIN_SEARCH_LENGTH = 3
const DEBOUNCE_MS = 250

/** The value, once it has stopped changing for a moment - so a search runs per pause, not per keystroke. */
export function useDebouncedValue<T>(value: T): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [value])
  return debounced
}

export function isSearchable(query: string): boolean {
  return query.trim().length >= MIN_SEARCH_LENGTH
}

interface WorkerSearchInputProps {
  id: string
  value: string
  onChange: (value: string) => void
  placeholder: string
}

export function WorkerSearchInput({ id, value, onChange, placeholder }: WorkerSearchInputProps) {
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="sr-only">
        Search workers
      </label>
      <input
        id={id}
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        className="w-full border border-marine/25 px-3 py-2 text-sm text-ink focus:outline-none focus:border-marine"
      />
    </div>
  )
}

function isAdminHit(hit: WorkerSearchHit): hit is AdminWorkerSearchHit {
  return 'clientId' in hit
}

/** A hit is unique by worker and client - the same worker id can exist under two clients. */
function hitKey(hit: WorkerSearchHit): string {
  return isAdminHit(hit) ? `${hit.clientId}/${hit.workerId}` : hit.workerId
}

const HIT_SORT: SortAccessors<WorkerSearchHit, 'name' | 'client' | 'facility' | 'tier' | 'score'> = {
  name: (hit) => hit.name,
  client: (hit) => (isAdminHit(hit) ? hit.organizationName : null),
  facility: (hit) => hit.facilityName,
  tier: (hit) => TIER_SORT_RANK[hit.tier],
  score: (hit) => hit.score,
}

interface WorkerSearchTableProps<Hit extends WorkerSearchHit> {
  found: SearchResults<Hit>
  /** What a search with no hits searched, e.g. "your facilities" - completes "No workers match … in ___". */
  scopeLabel: string
  /** When given, each row can be opened to show this beneath it. */
  renderDetail?: (hit: Hit) => ReactNode
}

/**
 * Search hits as a table: who (with their role under the name), which
 * facility (and client, for the platform admin's cross-client search), tier
 * and score.
 */
export function WorkerSearchTable<Hit extends WorkerSearchHit>({ found, scopeLabel, renderDetail }: WorkerSearchTableProps<Hit>) {
  const [openKey, setOpenKey] = useState<string | null>(null)
  // Sorts the page of results on screen; the search itself decides which results those are.
  const { sorted, header } = useSort<Hit, keyof typeof HIT_SORT>(found.results, HIT_SORT)

  if (found.results.length === 0) {
    return (
      <div className="bg-white border border-marine/15 p-4 text-xs text-ink-mid">
        No workers match “{found.query}” in {scopeLabel}.
      </div>
    )
  }

  const showClient = found.results.some(isAdminHit)
  const columnCount = showClient ? 5 : 4

  return (
    <div>
      <div className="text-[11px] text-ink-mid mb-2">
        {found.total > found.results.length
          ? `Showing the first ${found.results.length} of ${found.total.toLocaleString()} matches. Type more to narrow it down.`
          : `${found.total} ${found.total === 1 ? 'match' : 'matches'}`}
      </div>
      <div className="bg-white border border-marine/15 overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-marine/15 text-[10px] uppercase tracking-wide text-ink-mid">
              <SortableTh className="px-4 py-2" label="Worker" {...header('name')} />
              {showClient && <SortableTh className="px-3 py-2" label="Client" {...header('client')} />}
              <SortableTh className="px-3 py-2" label="Facility" {...header('facility')} />
              <SortableTh className="px-3 py-2" label="Tier" {...header('tier')} />
              <SortableTh className="px-4 py-2" label="Score" {...header('score')} alignRight />
            </tr>
          </thead>
          <tbody>
            {sorted.map((hit) => {
              const key = hitKey(hit)
              const isOpen = openKey === key
              return (
                <Fragment key={key}>
                  <tr
                    className={`border-b border-marine/10 ${renderDetail ? 'cursor-pointer hover:bg-canvas' : ''}`}
                    onClick={renderDetail ? () => setOpenKey(isOpen ? null : key) : undefined}
                  >
                    <td className="px-4 py-2">
                      <div className="flex items-start gap-2">
                        <WorkerIcon className="mt-0.5 w-4 h-4 text-marine" />
                        <div className="min-w-0">
                      {renderDetail ? (
                        <button type="button" aria-expanded={isOpen} className="font-bold text-ink text-left hover:text-marine">
                          {hit.name}
                        </button>
                      ) : (
                        <div className="font-bold text-ink">{hit.name}</div>
                      )}
                      <div className="flex items-center gap-1.5">
                        <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold rounded shrink-0 ${WORKER_ROLE_BADGE_CLASS}`} title={WORKER_ROLE_LABELS[hit.role]}>
                          {hit.role}
                        </span>
                      </div>
                        </div>
                      </div>
                    </td>
                    {showClient && <td className="px-3 py-2 text-ink">{isAdminHit(hit) ? hit.organizationName : ''}</td>}
                    <td className="px-3 py-2">
                      <div className="text-ink">{hit.facilityName}</div>
                    </td>
                    <td className="px-3 py-2 text-ink whitespace-nowrap">
                      <span className="inline-flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${TIER_BG_CLASS[hit.tier]}`} />
                        {TIER_LABELS[hit.tier]}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-ink text-right">{formatScore(hit.score)}</td>
                  </tr>
                  {isOpen && renderDetail && (
                    <tr>
                      <td colSpan={columnCount} className="p-0">
                        {renderDetail(hit)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
